import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

const NAV_ITEMS = [
  { to: "/", label: "首页", end: true },
  { to: "/category/movie", label: "电影", end: false },
  { to: "/category/tv", label: "电视剧", end: false },
  { to: "/news", label: "资讯", end: false },
  { to: "/about", label: "关于", end: false },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : "/search");
    setMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled || menuOpen
          ? "bg-background-950/90 backdrop-blur border-b border-background-800"
          : "bg-gradient-to-b from-background-950/85 to-transparent"
      }`}
    >
      <div className="w-full h-16 md:h-[68px] px-4 md:px-8 flex items-center gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" aria-label="西瓜影音首页">
          <span className="w-9 h-9 rounded-lg bg-primary-500 flex items-center justify-center">
            <i className="ri-movie-2-fill text-background-50 text-xl"></i>
          </span>
          <span className="font-heading text-xl md:text-2xl font-semibold tracking-wide text-foreground-50">
            西瓜<span className="text-primary-500">影音</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-4">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive
                    ? "text-primary-500 bg-primary-500/10"
                    : "text-foreground-200 hover:text-foreground-50 hover:bg-background-800/70"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <form
          onSubmit={submitSearch}
          className="hidden lg:flex items-center gap-2 ml-auto bg-background-800/70 border border-background-700 rounded-full pl-4 pr-1 py-1 w-64 focus-within:border-primary-500 transition-colors"
        >
          <i className="ri-search-line text-foreground-400 text-sm"></i>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索影片、演员、类型"
            className="flex-1 bg-transparent text-sm text-foreground-100 placeholder:text-foreground-500 outline-none"
            aria-label="搜索影视"
          />
          <button
            type="submit"
            className="w-8 h-8 rounded-full bg-primary-500 hover:bg-primary-600 flex items-center justify-center text-background-50 cursor-pointer transition-colors"
            aria-label="提交搜索"
          >
            <i className="ri-arrow-right-line text-sm"></i>
          </button>
        </form>

        <div className="ml-auto lg:ml-3 flex items-center gap-2">
          <Link
            to="/search"
            className="lg:hidden w-10 h-10 rounded-full bg-background-800/70 flex items-center justify-center text-foreground-200 hover:text-primary-500 cursor-pointer transition-colors"
            aria-label="搜索"
          >
            <i className="ri-search-line text-lg"></i>
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="md:hidden w-10 h-10 rounded-full bg-background-800/70 flex items-center justify-center text-foreground-100 cursor-pointer"
            aria-label="打开菜单"
            aria-expanded={menuOpen}
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-950 border-t border-background-800 px-4 py-4 animate-fade-in">
          <form
            onSubmit={submitSearch}
            className="flex items-center gap-2 bg-background-800/70 border border-background-700 rounded-full pl-4 pr-1 py-1 mb-4 focus-within:border-primary-500"
          >
            <i className="ri-search-line text-foreground-400 text-sm"></i>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影片、演员、类型"
              className="flex-1 bg-transparent text-sm text-foreground-100 placeholder:text-foreground-500 outline-none"
              aria-label="搜索影视"
            />
            <button
              type="submit"
              className="w-8 h-8 rounded-full bg-primary-500 flex items-center justify-center text-background-50 cursor-pointer"
              aria-label="提交搜索"
            >
              <i className="ri-arrow-right-line text-sm"></i>
            </button>
          </form>
          <div className="flex flex-col">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-md text-base font-medium ${
                    isActive ? "text-primary-500 bg-primary-500/10" : "text-foreground-200"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}